import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { FinancialCalculations, FinancialReview } from '@app/models';
import { FinancialService, QbService, ToastService } from '@app/services';
import { AppInfoService } from '@app/services/app-info.service';
import { NgxMaskDirective } from 'ngx-mask';
import { debounceTime, merge, switchMap, take, takeWhile } from 'rxjs';
import { ModalComponent } from '../../modal/modal.component';

@Component({
  selector: 'qb-financial-review',
  templateUrl: './financial-review.component.html',
  styleUrls: ['./financial-review.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatCheckbox,
    MatButton,
    ModalComponent,
    NgxMaskDirective,
  ],
})
export class FinancialReviewComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  @Input() disabled: boolean = false;

  financialReviewForm: FormGroup = this.formBuilder.group({
    largestPublicWork: ['0'],
    secondLargestPublicWork: ['0'],
    averageLargestPublicWork: ['0'],
    averageLargestPublicWorkWithMultiplier: ['0'],

    currentAssets: ['0'],
    currentLiabilities: ['0'],
    workingCapital: ['0'],

    secondCurrentAssets: ['0'],
    secondCurrentLiabilities: ['0'],
    currentRatio: ['0'],

    totalAssets: ['0'],
    totalLiabilities: ['0'],
    netWorth: ['0'],

    bondCapacity: ['0'],
    ratingAnalysisNotes: [''],

    financialReviewConfirmed: [false],
  });

  get largestPublicWork() {
    return this.financialReviewForm.get('largestPublicWork');
  }
  get secondLargestPublicWork() {
    return this.financialReviewForm.get('secondLargestPublicWork');
  }
  get averageLargestPublicWork() {
    return this.financialReviewForm.get('averageLargestPublicWork');
  }
  get averageLargestPublicWorkWithMultiplier() {
    return this.financialReviewForm.get('averageLargestPublicWorkWithMultiplier');
  }
  get currentAssets() {
    return this.financialReviewForm.get('currentAssets');
  }
  get currentLiabilities() {
    return this.financialReviewForm.get('currentLiabilities');
  }
  get workingCapital() {
    return this.financialReviewForm.get('workingCapital');
  }
  get secondCurrentAssets() {
    return this.financialReviewForm.get('secondCurrentAssets');
  }
  get secondCurrentLiabilities() {
    return this.financialReviewForm.get('secondCurrentLiabilities');
  }
  get currentRatio() {
    return this.financialReviewForm.get('currentRatio');
  }
  get totalAssets() {
    return this.financialReviewForm.get('totalAssets');
  }
  get totalLiabilities() {
    return this.financialReviewForm.get('totalLiabilities');
  }
  get netWorth() {
    return this.financialReviewForm.get('netWorth');
  }
  get bondCapacity() {
    return this.financialReviewForm.get('bondCapacity');
  }

  private rateFinalization: number;
  private proposedRate: number;
  private contractorName: string;
  private commentsToContractor: string;

  saving: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;

  componentActive = true;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private financialService: FinancialService,
    private qb: QbService,
    private app: AppInfoService
  ) {}

  ngOnInit(): void {
    this.financialService
      .getFinancialReview(this.app.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const { rateFinalization, proposedRate, contractorName, commentsToContractor, ...reviewData } = res;
          reviewData.averageLargestPublicWork = 0;
          reviewData.averageLargestPublicWorkWithMultiplier = 0;
          reviewData.workingCapital = 0;
          reviewData.secondCurrentAssets = reviewData.currentAssets;
          reviewData.secondCurrentLiabilities = reviewData.currentLiabilities;
          reviewData.currentRatio = 0.0;
          reviewData.netWorth = 0;

          this.rateFinalization = rateFinalization;
          this.proposedRate = proposedRate;
          this.contractorName = contractorName;
          this.commentsToContractor = commentsToContractor;

          this.formData = reviewData;
          this.financialReviewForm.patchValue(reviewData, { emitEvent: false });
          this.calculateTotals();

          this.streamFinancialReviewData(res);

          this.financialReviewForm.valueChanges
            .pipe(
              takeWhile(() => this.componentActive),
              debounceTime(200)
            )
            .subscribe(() => {
              this.streamFinancialReviewData();
            });
        },
        error: err => {
          console.log({ err });
        },
      });

    merge(this.currentAssets.valueChanges, this.secondCurrentAssets.valueChanges)
      .pipe(
        takeWhile(() => this.componentActive),
        debounceTime(50)
      )
      .subscribe(val => {
        // To prevent infinite loops, parse this value to a float first, and only stream the change if it's different from the current value.
        const numVal = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]+/g, '')) : val;
        if (this.currentAssets.value !== numVal) {
          (this.currentAssets as FormControl).setValue(numVal, { emitEvent: false });
        }
        if (this.secondCurrentAssets.value !== numVal) {
          (this.secondCurrentAssets as FormControl).setValue(numVal, { emitEvent: false });
        }
        this.financialReviewForm.updateValueAndValidity({ emitEvent: false });
        this.calculateTotals();
      });

    merge(this.currentLiabilities.valueChanges, this.secondCurrentLiabilities.valueChanges)
      .pipe(
        takeWhile(() => this.componentActive),
        debounceTime(50)
      )
      .subscribe(val => {
        const numVal = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]+/g, '')) : val;
        if (this.currentLiabilities.value !== numVal) {
          (this.currentLiabilities as FormControl).setValue(numVal, { emitEvent: false });
        }
        if (this.secondCurrentLiabilities.value !== numVal) {
          (this.secondCurrentLiabilities as FormControl).setValue(numVal, { emitEvent: false });
        }
        this.financialReviewForm.updateValueAndValidity({ emitEvent: false });
        this.calculateTotals();
      });

    if (this.disabled || this.app.status.isComplete()) {
      this.financialReviewForm.disable({ onlySelf: true });
    }
  }

  ngOnDestroy() {
    this.componentActive = false;
  }

  streamFinancialReviewData(data?: FinancialReview) {
    let financialReview: FinancialReview = {
      applicationId: this.app.id,
      // rateFinalization: this.rateFinalization,
      // proposedRate: this.proposedRate, // Proposed Rate should be calculated on the backend, not entrusted to the frontend calculations.
      commentsToContractor: this.commentsToContractor,
      contractorName: this.contractorName,
      currentAssets: this.currentAssets.value ?? 0,
      currentLiabilities: this.currentLiabilities.value ?? 0,
      totalAssets: this.totalAssets.value ?? 0,
      totalLiabilities: this.totalLiabilities.value ?? 0,
      largestPublicWork: this.largestPublicWork.value,
      secondLargestPublicWork: this.secondLargestPublicWork.value,
      bondCapacity: this.bondCapacity.value,
      financialReviewConfirmed: this.financialReviewForm.get('financialReviewConfirmed').value,
      ratingAnalysisNotes: this.financialReviewForm.get('ratingAnalysisNotes').value,
    };

    if (data) {
      financialReview = { ...financialReview, ...data };
    }
    // (financialReview as any).rand = Math.random() * 100000;
    this.financialService.financialReviewData.next(financialReview);
  }

  onSaveClick(): void {
    this.saving = true;
    this.qb.loading = true;

    this.financialService.financialReviewData
      .pipe(
        take(1),
        switchMap(financialReview => {
          return this.financialService
            .saveFinancialReview(this.app.id, financialReview)
            .pipe(takeUntilDestroyed(this.destroyRef));
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Financial review saved.', 'Saved Successfully');
          this.qb.loading = false;
          this.saving = false;
        },
        error: err => {
          console.log({ err });
          this.toastService.error(err.publicMessage, 'Error');
          this.qb.loading = false;
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
          this.qb.loading = false;
        },
      });
  }

  onCancel(modal: ModalComponent): void {
    modal.data = {
      content:
        "Are you sure you'd like to undo any changes you have made to the Rating Analysis section of this application?",
    };
    modal.open();
  }

  confirmReset(modal: ModalComponent) {
    // reset form
    this.financialReviewForm.setValue(this.formData);
    modal.close();
  }

  calculateTotals(): void {
    this.averageLargestPublicWork.setValue((this.largestPublicWork.value + this.secondLargestPublicWork.value) / 2);

    const multiplierForLargestPublicWork = this.averageLargestPublicWork.value < 10000000 ? 0.33 : 0.5;
    this.averageLargestPublicWorkWithMultiplier.setValue(
      this.averageLargestPublicWork.value * multiplierForLargestPublicWork
    );

    this.workingCapital.setValue(Math.max(this.currentAssets.value - this.currentLiabilities.value, 0) * 8);

    let calculatedRatio = '-';
    if (this.currentLiabilities.value > 0) {
      const assestsAndLiabilities = this.secondCurrentAssets.value / this.secondCurrentLiabilities.value;
      calculatedRatio = (Math.round((assestsAndLiabilities + Number.EPSILON) * 100) / 100).toString();
    }
    this.currentRatio.setValue(calculatedRatio);

    this.netWorth.setValue(Math.max(this.totalAssets.value - this.totalLiabilities.value, 0) * 8);

    // If the contractor is a small business, we only need to consider the bond capacity and the average largest public work.
    let proposedRate;
    if (this.app.smallBusiness) {
      proposedRate = Math.min(+this.bondCapacity.value, this.averageLargestPublicWorkWithMultiplier.value);
    } else {
      proposedRate = Math.min(
        +this.bondCapacity.value,
        this.netWorth.value,
        this.averageLargestPublicWorkWithMultiplier.value,
        this.workingCapital.value
      );
    }

    //Round the proposedRate to the nearest 1000
    this.proposedRate = Math.round(proposedRate / 1000) * 1000;
    const calculations: FinancialCalculations = {
      contractorName: this.contractorName,
      averageLargestPublicWorkWithMultiplier: this.averageLargestPublicWorkWithMultiplier.value,
      workingCapital: this.workingCapital.value,
      netWorth: this.netWorth.value,
      bondCapacity: this.bondCapacity.value,
      proposedRate: this.proposedRate,
      commentsToContractor: this.commentsToContractor,
      rateFinalization: this.rateFinalization,
    };

    this.financialService.financialCalculations.next(calculations);
  }
}
